/**
 * PainLocationPicker — Inline pain follow-up (area → side → duration).
 *
 * Calm body-area selector with optional side, plus a duration prompt.
 * NOT a medical UI. Tone is supportive and academy-friendly.
 *
 * Props
 *   value      — { area: string|null, side: "left"|"right"|null, duration: "days"|"weeks"|"long"|null }
 *   onChange   — (next: { area, side, duration }) => void
 */
import React from "react";
import { useTranslation } from "react-i18next";

const AREAS = [
  { id: "knee",    label: "Knä",     paired: true },
  { id: "ankle",   label: "Fotled",  paired: true },
  { id: "thigh",   label: "Lår",     paired: true },
  { id: "hip",     label: "Höft",    paired: true },
  { id: "back",    label: "Rygg",    paired: false },
  { id: "shoulder",label: "Axel",    paired: true },
  { id: "groin",   label: "Ljumske", paired: true },
  { id: "calf",    label: "Vad",     paired: true },
  { id: "other",   label: "Annat",   paired: false },
];

// Areas where a side selector is meaningful (paired body parts).
const SIDED_AREAS = new Set(
  AREAS.filter((a) => a.paired).map((a) => a.id)
);

const SIDES = [
  { id: "left", label: "Vänster" },
  { id: "right", label: "Höger" },
  { id: "both", label: "Båda" },
];

const DURATIONS = [
  { id: "today", label: "Idag" },
  { id: "days",  label: "Några dagar" },
  { id: "weeks", label: "1–2 veckor" },
  { id: "long",  label: "Längre tid" },
];

export default function PainLocationPicker({
  value = { area: null, side: null, duration: null },
  onChange,
}) {
  const { i18n } = useTranslation();
  const en = i18n.language?.startsWith('en');
  const labels = { knee: 'Knee', ankle: 'Ankle', thigh: 'Thigh', hip: 'Hip', back: 'Back', shoulder: 'Shoulder', groin: 'Groin', calf: 'Calf', other: 'Other', left: 'Left', right: 'Right', both: 'Both', today: 'Today', days: 'A few days', weeks: '1–2 weeks', long: 'Longer' };
  const current = value || { area: null, side: null, duration: null };

  const setArea = (id) => {
    const next = {
      area: id,
      side: SIDED_AREAS.has(id) ? current.side : null,
      duration: current.duration,
    };
    onChange?.(next);
  };
  const setSide = (s) => onChange?.({ ...current, side: s });
  const setDuration = (d) => onChange?.({ ...current, duration: d });
  const showSide = current.area && SIDED_AREAS.has(current.area);
  const showDuration = !!current.area;

  return (
    <div
      data-testid="pain-location-picker"
      className="rounded-2xl border border-rose-200/70 bg-[rgb(var(--tn-tint-fff1f2))]/40 dark:border-rose-500/30 dark:bg-rose-950/20 p-5"
    >
      <p className="text-sm font-medium text-rose-900 dark:text-[rgb(var(--tn-text-fecdd3))]">
        {en ? 'Where do you feel it most?' : 'Var känns det mest?'}
      </p>
      <p className="mt-1 text-xs text-rose-800/70 dark:text-[rgb(var(--tn-text-fda4af))]/70 leading-relaxed">
        {en ? 'Choose where you feel it most right now.' : 'Välj var det känns mest just nu.'}
      </p>

      <div className="mt-4 grid grid-cols-3 gap-2">
        {AREAS.map((a) => {
          const active = current.area === a.id;
          return (
            <button
              key={a.id}
              type="button"
              data-testid={`pain-area-${a.id}`}
              onClick={() => setArea(a.id)}
              className={`h-11 rounded-xl text-sm font-medium transition-all active:scale-[0.97] ${
                active
                  ? "bg-rose-600 text-white border border-rose-600 shadow-[0_4px_14px_rgba(225,29,72,0.20)]"
                  : "bg-white border border-rose-200 text-rose-900 hover:border-rose-400 dark:bg-[rgb(var(--tn-bg-18181b))] dark:border-rose-900/40 dark:text-[rgb(var(--tn-text-fecdd3))]"
              }`}
            >
              {en ? labels[a.id] : a.label}
            </button>
          );
        })}
      </div>

      {showSide && (
        <div className="mt-5" data-testid="pain-side-row">
          <p className="text-[11px] uppercase tracking-wide text-rose-800/80 dark:text-[rgb(var(--tn-text-fda4af))]/80 font-medium mb-2">
            Sida
          </p>
          <div className="grid grid-cols-3 gap-2">
            {SIDES.map((s) => {
              const active = current.side === s.id;
              return (
                <button
                  key={s.id}
                  type="button"
                  data-testid={`pain-side-${s.id}`}
                  onClick={() => setSide(s.id)}
                  className={`h-11 rounded-xl text-sm font-medium transition-all active:scale-[0.97] ${
                    active
                      ? "bg-rose-600 text-white border border-rose-600"
                      : "bg-white border border-rose-200 text-rose-900 dark:bg-[rgb(var(--tn-bg-18181b))] dark:border-rose-900/40 dark:text-[rgb(var(--tn-text-fecdd3))]"
                  }`}
                >
                  {en ? labels[s.id] : s.label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {showDuration && (
        <div className="mt-5" data-testid="pain-duration-row">
          <p className="text-[11px] uppercase tracking-wide text-rose-800/80 dark:text-[rgb(var(--tn-text-fda4af))]/80 font-medium mb-2">
            {en ? 'How long have you felt this?' : 'Hur länge har du känt det?'}
          </p>
          <div className="grid grid-cols-3 gap-2">
            {DURATIONS.map((d) => {
              const active = current.duration === d.id;
              return (
                <button
                  key={d.id}
                  type="button"
                  data-testid={`pain-duration-${d.id}`}
                  onClick={() => setDuration(d.id)}
                  className={`h-11 rounded-xl text-sm font-medium transition-all active:scale-[0.97] ${
                    active
                      ? "bg-rose-600 text-white border border-rose-600"
                      : "bg-white border border-rose-200 text-rose-900 dark:bg-[rgb(var(--tn-bg-18181b))] dark:border-rose-900/40 dark:text-[rgb(var(--tn-text-fecdd3))]"
                  }`}
                >
                  {en ? labels[d.id] : d.label}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export { AREAS as PAIN_AREAS, SIDED_AREAS, SIDES as PAIN_SIDES, DURATIONS as PAIN_DURATIONS };
